import { ContentSyncCoordinator, type ContentSyncConfig, type SyncPassResult } from "./content-sync";
import { tradeAlerts } from "@/services/cache";
import { tradeAlertsSyncKey } from "@/types/products/product-keys";
import { goFetch } from "@/utils/network-utils";
import { formatDateAsISO8601String, utcNow } from "@tmi/utils/date-time";
import type { ContentfulTradeAlert, NormalizedContent } from "@tmi/types/entities";
import type { TradeAlerts } from "@tmi/types/api/content-models";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";

import { createDefaultLogger, LogLevels } from "@/types/console-logger";
const logger = createDefaultLogger( LogLevels.DEBUG );

const config: ContentSyncConfig<TradeAlerts, ContentfulTradeAlert> = {
    name: 'trade-alerts',
    store: () => tradeAlerts,
    syncKey: tradeAlertsSyncKey,
    metaQueryId: 'cbd5256ca0dcc0879c5601c9aaa8e0bd',
    contentQueryId: '210dba7500d8d488244bae6c7abe7efc',
    contentPageSize: 500,
    metaContentFilter: { fromPublishedDateTimeUTC: '2008-01-01' },
    extractMetaPayload: ( data ) => data.tradeAlertCatalog?.trade_alerts,
    extractContentPayload: ( data ) => data.tradeAlertCatalog?.trade_alerts,
    normalizeItem: normalizeTradeAlert
};

export class TradeAlertsSyncCoordinator {
    readonly #inner: ContentSyncCoordinator<TradeAlerts, ContentfulTradeAlert>;
    constructor() {
        this.#inner = new ContentSyncCoordinator( config );
    }
    patchRepository( authToken: string, force: boolean = false ): Promise<SyncPassResult> {
        return this.#inner.patchRepository( authToken, force );
    }
}

/** The head check asks for the newest alerts only: those published in this window, at most HEAD_LIMIT of them. */
const HEAD_WINDOW_IN_DAYS = 3;
const HEAD_LIMIT = 50;

/**
 * gap_alert_delivery-1: the full pass above re-reads every alert's metadata back to 2008 and then locks itself for
 * five minutes, so a catch-up pass (the tab visible again, the browser back online) could not show a new alert for
 * up to that long. This asks for just the newest alerts, through the same delta query the store falls back to
 * (fromPublishedDateTimeUTC, limit 50), and writes only the rows the cache does not hold yet or holds an older edit
 * of. Anything beyond the window or the limit is the full pass's job, as is a cold cache.
 *
 * @returns how many rows were written (the caller reports an update only when this is above 0), or null when the
 *          request failed
 */
export async function patchTradeAlertHead( authToken: string ): Promise< number | null > {
    const store = config.store();
    if ( await store.isEmpty() ) {
        return 0;
    }

    const response = await goFetch< TradeAlerts, PersistedQueryPayload >( {
        serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
        endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
        payload: {
            id: config.contentQueryId,
            variables: {
                skip: 0,
                limit: HEAD_LIMIT,
                contentFilter: {
                    fromPublishedDateTimeUTC: formatDateAsISO8601String( utcNow().minus( { days: HEAD_WINDOW_IN_DAYS } ), true )
                }
            }
        },
        authToken
    } );

    const received = response ? config.extractContentPayload( response.data ) : null;
    if ( null == received ) {
        return null;
    }

    const changed: NormalizedContent[] = [];
    for ( const alert of received ) {
        const normalized = normalizeTradeAlert( alert );
        if ( !normalized ) {
            continue;
        }

        // Same currency test as the full pass: an unchanged row is not rewritten, so no update is reported for it.
        const cached = await store.get( normalized.contentfulId );
        if ( 0 === cached?.updatedDate?.localeCompare( normalized.updatedDate ?? '' ) ) {
            continue;
        }
        changed.push( normalized );
    }

    if ( changed.length > 0 ) {
        await store.putMany( changed );
        logger.info( `[ trade-alerts-sync-coordinator ] head check wrote ${ changed.length } trade alert(s)` );
    }

    return changed.length;
}

export function normalizeTradeAlert( alert: ContentfulTradeAlert ): NormalizedContent | undefined {
    try {
        return {
            contentfulId: alert.id,
            slug: alert.slug,
            publishedDate: alert.published_date,
            updatedDate: alert.publish_at,
            title: alert.title,
            text: alert.body,
            body2: alert.body2,
            videoLink: alert.video_link,
            isFeatured: alert.is_featured,
            preview: alert.preview,
            thumbnail: alert.thumbnail,
            editorialAd: alert.editorial_ad ?? undefined,
            authorId: alert.author_id,
            adProductId: alert.ad_product_id,
            categoryIds: alert.assoc_category_ids ?? [],
            productIds: alert.assoc_product_ids ?? [],
            sectorIds: alert.assoc_sector_ids ?? [],
            tickerIds: alert.assoc_ticker_ids ?? []
        };
    } catch ( e: any ) {
        logger.error( `[ trade-alerts-sync-coordinator ] Failed to normalize trade alert '${ alert.id }':`, e );
        return undefined;
    }
}
